import { ForwardedRef, forwardRef } from 'react';
import { mergeProps } from 'react-aria';
import clsx from 'clsx';
import {
  Link as AriaLink,
  type LinkProps as AriaLinkProps,
  Button,
  type ButtonProps as AriaButtonProps,
} from 'react-aria-components';

import styles from './Link.module.css';

export type CommonProps = Pick<AriaLinkProps, 'id' | 'aria-label' | 'isDisabled' | 'slot'> & {
  replace?: boolean;
  title?: string;
  decoration?: 'all' | 'text' | 'none';
  color?: 'inherit';
};

export type ButtonProps = {
  href?: undefined;
  children?: AriaButtonProps['children'];
  onPress: AriaButtonProps['onPress'];
};
export type LinkProps = {
  href: string;
  children?: AriaLinkProps['children'];
  onPress?: AriaLinkProps['onPress'];
  /**
   * `_blank` opens an in-app page in a new tab, for a page whose own state (an unsaved selection,
   * a half-filled list) would be lost by navigating away. External links open in a new tab anyway.
   */
  target?: '_blank';
};

export type Props = CommonProps & (ButtonProps | LinkProps);

export const Link = forwardRef(function _Link(props: Props, ref: ForwardedRef<HTMLAnchorElement>) {
  if (props.href === undefined) {
    const { children, decoration = 'text', color, ...rest } = props;
    return (
      <Button
        {...mergeProps(
          { className: clsx(styles.root, styles[`decoration-${decoration}`], styles[`color-${color}`]) },
          rest,
        )}
      >
        {children}
      </Button>
    );
  }

  const { replace, href, children, decoration = 'text', color, ...rest } = props;
  const routerOptions = replace ? { replace } : undefined;
  const isPdf = href ? /\.pdf\/?/.test(href) : false;
  const isExternal = href ? href.startsWith('http') : false;
  const linkProps = isPdf
    ? { download: true }
    : isExternal
      ? {
          target: '_blank',
          rel: 'noopener noreferrer',
        }
      : {};

  return (
    <AriaLink
      href={href}
      routerOptions={routerOptions}
      {...linkProps}
      {...mergeProps(
        { className: clsx(styles.root, styles[`decoration-${decoration}`], styles[`color-${color}`]), ref },
        rest,
      )}
    >
      {children}
    </AriaLink>
  );
});
